import { ArrowRight, BarChart3, Boxes, ClipboardList, HeartPulse, MessageCircle, Network, PackageCheck, ScanLine, ShieldCheck, Sparkles, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CareImage, SectionHeading } from './SectionHeading';

const CLINICAL_STEPS = [
  ['Understand the person.', 'Bring clinical profiles and voluntarily shared health information into the conversation.'],
  ['Review with context.', 'Validate prescriptions, review costing and use drug-interaction decision support alongside professional judgement.'],
  ['Make the next step clear.', 'Staff prioritise requests. Pharmacists review them. Patients approve quotes before collection.'],
  ['Keep the conversation going.', 'Connect visits through eligible refill requests and conversations with the pharmacy team.'],
];

export const ClinicalSection = () => (
  <section className="wl-section" id="clinical" aria-labelledby="clinical-heading" data-testid="welcome-clinical-section">
    <div className="wl-wrap">
      <div className="wl-heading-row">
        <SectionHeading id="clinical-heading" eyebrow="01 / CARE COMES FIRST" title={<>A patient. Not just<br />a prescription.</>} />
        <p className="wl-heading-aside">The best pharmacy care starts with understanding.<br />Give your team the context to make each interaction count.</p>
      </div>
      <div className="wl-clinical-grid">
        <CareImage name="clinical" alt="Illustrative pharmacist and patient reviewing a prescription together in a Ghanaian pharmacy consultation room" caption="Room for a better conversation." />
        <div className="wl-clinical-steps">
          {CLINICAL_STEPS.map(([title, body], i) => <article key={title} className="wl-care-step" data-testid={`welcome-clinical-step-${i}`}>
            <span className="wl-step-number">0{i + 1}</span><div><h3>{title}</h3><p>{body}</p></div>
          </article>)}
        </div>
      </div>
      <div className="wl-ai-note" data-testid="welcome-clinical-oversight"><Sparkles size={20} /><p><strong>Intelligence that assists. Expertise that leads.</strong> AI supports the pharmacist. It does not diagnose, prescribe autonomously or replace professional judgement.</p><span>HUMAN-LED, ALWAYS</span></div>
    </div>
  </section>
);

const PATIENT_TOOLS = [
  [Upload, 'Prescriptions, shared', 'Upload a prescription for the pharmacy team to review.'],
  [PackageCheck, 'Refills, considered', 'Request eligible refills and approve your quote.'],
  [MessageCircle, 'Conversations, continued', 'Talk to your pharmacy team between visits.'],
  [HeartPulse, 'Health readings, by choice', 'Voluntarily share readings to add context to your care.'],
];

export const PatientSection = () => (
  <section className="wl-section wl-patient-section" id="portal" aria-labelledby="portal-heading" data-testid="welcome-portal-section">
    <div className="wl-wrap wl-patient-grid">
      <div>
        <SectionHeading id="portal-heading" eyebrow="03 / CONNECTED CARE" title={<>Care doesn’t end<br />at the counter.</>}>A patient portal that keeps the next step within reach — from the first prescription upload to the next conversation.</SectionHeading>
        <div className="wl-patient-tools">{PATIENT_TOOLS.map(([Icon, title, body], i) => <div key={title} data-testid={`welcome-patient-tool-${i}`}><Icon size={19} /><div><h3>{title}</h3><p>{body}</p></div></div>)}</div>
        <div className="wl-privacy-note" data-testid="welcome-privacy-note"><ShieldCheck size={18} /><p>Patient information follows permissions and explicit sharing choices. Records are not automatically shared across every pharmacy. Portal activity requires connectivity.</p></div>
      </div>
      <CareImage name="patient" alt="Illustrative Ghanaian patient using her phone at home between pharmacy visits; no patient information is displayed" caption="Between visits. Still connected." />
    </div>
  </section>
);

const OPERATIONS = [
  [ScanLine, 'Point of sale', 'Barcode scanning, held carts, receipts and day-to-day counter workflows.'],
  [Boxes, 'Inventory & expiry', 'Batch visibility, expiry alerts and stock counts that support dispensing.'],
  [ClipboardList, 'Procurement', 'Purchase orders, approvals, supplier returns and replenishment planning.'],
  [BarChart3, 'Finance & reporting', 'Ledgers, daily summaries and Ghana-focused tax and payroll tools.'],
  [Network, 'Multi-branch management', 'Branch operations and reporting, with access shaped by staff permissions.'],
  [PackageCheck, 'Dispensing support', 'Prescription review and costing connected to the operational workflow.'],
];

export const OperationsSection = () => (
  <section className="wl-section wl-operations" id="features" aria-labelledby="operations-heading">
    <div className="wl-wrap">
      <div className="wl-heading-row"><SectionHeading id="operations-heading" eyebrow="04 / THE EVERYDAY FOUNDATION" title="Behind better care, a connected pharmacy." /><Link className="wl-text-link" to="/plans" data-testid="welcome-operations-pricing">Find your plan <ArrowRight size={17} /></Link></div>
      <div className="wl-operations-grid">{OPERATIONS.map(([Icon, title, body], i) => <article key={title} data-testid={`welcome-operation-${i}`}><Icon size={24} /><h3>{title}</h3><p>{body}</p></article>)}</div>
      <p className="wl-small wl-operation-note" data-testid="welcome-plan-availability">Capabilities and limits vary by plan. Clinical, reporting, purchasing and stock-transfer workflows are not all available offline.</p>
    </div>
  </section>
);